import React, { useState, useEffect, useMemo } from 'react';
import { Slider } from '@heroui/react';
import { 
	trackExceptionWithContext,
	trackEventWithContext 
} from '../../../services/application-insights/appInsights';
import { 
	AppInsightsComponents, 
	AppInsightsErrorTypes,
	AppInsightsActions 
} from '../../../services/application-insights/appInsightsConstants';

export interface DateRangeSliderProps {
	/** Minimum date in YYYYMM format (e.g., 202201) */
	minDate?: number;
	/** Maximum date in YYYYMM format (e.g., 202508) */
	maxDate?: number;
	/** Current selected range in YYYYMM format */
	value?: [number, number];
	/** Callback when range changes */
	onChange?: (range: [number, number]) => void;
	/** Whether the slider is disabled */
	disabled?: boolean;
	/** Custom CSS class */
	className?: string;
	/** Show labels for min/max dates */
	darkMode?: boolean;
}

/**
 * Converts YYYYMM format to a sequential number for slider calculations
 * Example: 202201 -> 0, 202202 -> 1, etc.
 */
const dateToSliderValue = (yearMonth: number, minDate: number): number => {
	try {
		const year = Math.floor(yearMonth / 100);
		const month = yearMonth % 100;
		const minYear = Math.floor(minDate / 100);
		const minMonth = minDate % 100;

		return (year - minYear) * 12 + (month - minMonth);
	} catch (error) {
		// Only log exceptions for date conversion failures
		trackExceptionWithContext(
			error instanceof Error ? error : new Error('Date to slider value conversion failed'),
			{
				component: AppInsightsComponents.DATE_RANGE_SLIDER,
				additionalData: {
					errorType: AppInsightsErrorTypes.DATE_CONVERSION_ERROR,
					yearMonth: yearMonth,
					minDate: minDate
				}
			}
		);
		return 0; // Default fallback
	}
};

/**
 * Converts slider value back to YYYYMM format
 */
const sliderValueToDate = (sliderValue: number, minDate: number): number => {
	try {
		const minYear = Math.floor(minDate / 100);
		const minMonth = minDate % 100;

		const totalMonths = sliderValue + minMonth - 1;
		const year = minYear + Math.floor(totalMonths / 12);
		const month = (totalMonths % 12) + 1;

		return year * 100 + month;
	} catch (error) {
		// Only log exceptions for slider to date conversion failures
		trackExceptionWithContext(
			error instanceof Error ? error : new Error('Slider value to date conversion failed'),
			{
				component: AppInsightsComponents.DATE_RANGE_SLIDER,
				additionalData: {
					errorType: AppInsightsErrorTypes.DATE_CONVERSION_ERROR,
					sliderValue: sliderValue,
					minDate: minDate
				}
			}
		);
		return minDate; // Default fallback to minimum date
	}
};

/**
 * Gets the current date in YYYYMM format
 */
const getCurrentYearMonth = (): number => {
	const now = new Date();
	return now.getFullYear() * 100 + (now.getMonth() + 1);
};

/**
 * Formats YYYYMM to readable string
 */
const formatYearMonth = (yearMonth: number): string => {
	const year = Math.floor(yearMonth / 100);
	const month = yearMonth % 100;
	const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	return `${monthNames[month - 1]}-${year.toString().slice(-2)}`;
};

/**
 * Formats YYYYMM to readable string with day for tooltips
 */
const formatYearMonthWithDay = (yearMonth: number): string => {
	const year = Math.floor(yearMonth / 100);
	const month = yearMonth % 100;
	const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

	// Get the last day of the month
	const lastDayOfMonth = new Date(year, month, 0).getDate();

	return `${lastDayOfMonth}-${monthNames[month - 1]}-${year.toString().slice(-2)}`;
};

/**
 * Formats YYYYMM to readable string with first day for start date tooltips
 */
const formatYearMonthWithFirstDay = (yearMonth: number): string => {
	const year = Math.floor(yearMonth / 100);
	const month = yearMonth % 100;
	const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

	return `01-${monthNames[month - 1]}-${year.toString().slice(-2)}`;
};

/**
 * Formats YYYYMM to readable string with current day for live data
 */
const formatCurrentYearMonth = (yearMonth: number): string => {
	const year = Math.floor(yearMonth / 100);
	const month = yearMonth % 100;
	const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

	// Get current day for live data
	const currentDay = new Date().getDate();

	return `${currentDay}-${monthNames[month - 1]}-${year.toString().slice(-2)}`;
};

const DateRangeSlider: React.FC<DateRangeSliderProps> = ({
	minDate = 202201,
	maxDate = 202508,
	value = [minDate, maxDate],
	onChange,
	disabled = false,
	className = '',
	darkMode = false,
}) => {
	const [localValue, setLocalValue] = useState<[number, number]>(value);
	const [isLargeScreen, setIsLargeScreen] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth >= 1920 : true);
	const [hoveredThumb, setHoveredThumb] = useState<number | null>(null);

	// Handle window resize for responsive marks
	useEffect(() => {
		const handleResize = () => {
			setIsLargeScreen(window.innerWidth >= 1920);
		};

		if (typeof window !== 'undefined') {
			window.addEventListener('resize', handleResize);
			// Set initial value
			handleResize();

			return () => window.removeEventListener('resize', handleResize);
		}
	}, []);

	// Calculate current date and live range
	const currentYearMonth = getCurrentYearMonth();

	// Calculate slider range - add 1 extra step for "live"
	// Live selection should be one step more from maxDate to current month
	const maxSliderValue = dateToSliderValue(maxDate, minDate) + 1; // +1 for live step

	// Convert current values to slider values
	const sliderValue = useMemo(() => {
		const startSlider = dateToSliderValue(localValue[0], minDate);
		let endSlider = dateToSliderValue(localValue[1], minDate);

		// If the end value is the current month, map it to the live step
		if (localValue[1] === currentYearMonth) {
			endSlider = maxSliderValue;
		}

		return [startSlider, endSlider];
	}, [localValue, minDate, maxSliderValue, currentYearMonth]);

	// Update local value when prop changes
	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	const handleSliderChange = (newValue: number | number[]) => {
		try {
			if (Array.isArray(newValue) && newValue.length === 2) {
				let startDate = sliderValueToDate(newValue[0], minDate);
				let endDate;

				// If the end slider is at the live position (maxSliderValue), set to current month
				if (newValue[1] === maxSliderValue) {
					endDate = currentYearMonth;
				} else {
					endDate = sliderValueToDate(newValue[1], minDate);
				}

				const newDateRange: [number, number] = [startDate, endDate];
				setLocalValue(newDateRange);

				// Track slider interaction (key user event)
				trackEventWithContext(
					AppInsightsActions.DATE_RANGE_SLIDER_CHANGE,
					{
						component: AppInsightsComponents.DATE_RANGE_SLIDER,
						additionalData: {
							startDate: startDate,
							endDate: endDate,
							isLiveSelected: newValue[1] === maxSliderValue,
							sliderStartValue: newValue[0],
							sliderEndValue: newValue[1],
							source: 'slider-drag'
						}
					}
				);
			}
		} catch (error) {
			// Only log exceptions for slider change failures
			trackExceptionWithContext(
				error instanceof Error ? error : new Error('Slider value change failed'),
				{
					component: AppInsightsComponents.DATE_RANGE_SLIDER,
					additionalData: {
						errorType: AppInsightsErrorTypes.DATE_RANGE_SLIDER_ERROR,
						newValue: Array.isArray(newValue) ? newValue : [newValue],
						minDate: minDate,
						maxSliderValue: maxSliderValue
					}
				}
			);
		}
	};

	const handleSliderChangeEnd = (newValue: number | number[]) => {
		try {
			if (Array.isArray(newValue) && newValue.length === 2) {
				let startDate = sliderValueToDate(newValue[0], minDate);
				let endDate;

				// If the end slider is at the live position (maxSliderValue), set to current month
				if (newValue[1] === maxSliderValue) {
					endDate = currentYearMonth;
				} else {
					endDate = sliderValueToDate(newValue[1], minDate);
				}

				const newDateRange: [number, number] = [startDate, endDate];
				onChange?.(newDateRange);

				// Track completed slider selection (key user event)
				trackEventWithContext(
					AppInsightsActions.DATE_RANGE_SELECTION,
					{
						component: AppInsightsComponents.DATE_RANGE_SLIDER,
						additionalData: {
							startDate: startDate,
							endDate: endDate,
							isLiveSelected: newValue[1] === maxSliderValue,
							sliderStartValue: newValue[0],
							sliderEndValue: newValue[1],
							source: 'slider-complete'
						}
					}
				);
			}
		} catch (error) {
			// Only log exceptions for slider change end failures
			trackExceptionWithContext(
				error instanceof Error ? error : new Error('Slider change end failed'),
				{
					component: AppInsightsComponents.DATE_RANGE_SLIDER,
					additionalData: {
						errorType: AppInsightsErrorTypes.DATE_RANGE_SLIDER_ERROR,
						newValue: Array.isArray(newValue) ? newValue : [newValue],
						minDate: minDate,
						maxSliderValue: maxSliderValue
					}
				}
			);
		}
	};

	// Create marks for better UX - show all months on large screens, quarterly on smaller screens
	const marks = useMemo(() => {
		const marksArray = [];
		let currentDate = minDate;
		let monthCount = 0;

		while (currentDate <= maxDate) {
			const sliderVal = dateToSliderValue(currentDate, minDate);

			// Show all months on large screens (>=1920px), quarterly on smaller screens
			const shouldShowMark = isLargeScreen ? monthCount % 1 === 0 : monthCount % 4 === 0;

			if (shouldShowMark) {
				marksArray.push({
					value: sliderVal,
					label: formatYearMonth(currentDate),
				});
			}

			// Move to next month
			const year = Math.floor(currentDate / 100);
			const month = currentDate % 100;

			if (month === 12) {
				// Move to January of next year
				currentDate = (year + 1) * 100 + 1;
			} else {
				// Move to next month in same year
				currentDate = year * 100 + (month + 1);
			}

			monthCount++;
		}

		return [...marksArray, { value: maxSliderValue, label: 'Live' }];
	}, [minDate, maxDate, maxSliderValue, isLargeScreen]);

	return (
		<div className={`${className}`}>
			<Slider
				size="md"
				step={1}
				minValue={0}
				maxValue={maxSliderValue}
				value={sliderValue}
				onChange={handleSliderChange}
				onChangeEnd={handleSliderChangeEnd}
				isDisabled={disabled}
				className="max-w-full cursor-pointer"
				aria-label="Date range selector"
				classNames={{
					base: 'w-full',
					track: 'bg-white border-s-white',
					filler: 'bg-linear-to-r from-msi-orange-color to-msi-orange-color',
					step: 'data-[in-range=true]:bg-black/40 dark:data-[in-range=true]:bg-white/50',
					value: 'hidden',
					//thumb: 'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-msi-orange-color',
					mark: `${darkMode ? 'text-white' : 'text-black'} font-thin text-[10px] opacity-70 2xl:text-[8px]`,
				}}
				showSteps
				marks={marks}
				renderThumb={(props) => (
					<div
						{...props}
						className={`w-6 h-6 group p-1 top-1/2 ${props.index === 0 ? 'bg-msi-orange-color' : 'bg-msi-midblue-color-500'} text-md border-2 border-white rounded-full cursor-grab data-[dragging=true]:cursor-grabbing relative`}
						onMouseEnter={() => setHoveredThumb(props.index ?? null)}
						onMouseLeave={() => setHoveredThumb(null)}
					>
						{/* Tooltip balloon showing the selected value - conditionally rendered on hover */}
						{hoveredThumb === props.index && (
							<div
								className={`absolute ${props.index === 0 ? '-top-10' : '-top-16'} left-1/2 transform -translate-x-1/2 bg-white px-2 py-1 rounded whitespace-nowrap`}
							>
								{props.index === 0 ? (
									<div>
										<span className="font-bold">Analytics from: </span>
										<span>{formatYearMonthWithFirstDay(localValue[0])}</span>
									</div>
								) : (
									<div className="flex flex-col gap-2">
										<div>
											<span className="font-bold">Analytics to: </span>
											<span>
												{localValue[1] === currentYearMonth
													? formatYearMonthWithDay(maxDate)
													: formatYearMonthWithDay(localValue[1])}
											</span>
										</div>
										<div>
											<span className="font-bold">Map indicators: </span>
											<span>
												{localValue[1] === currentYearMonth
													? formatCurrentYearMonth(currentYearMonth)
													: formatYearMonthWithDay(localValue[1])}
											</span>
										</div>
									</div>
								)}
								{/* Small arrow pointing down */}
								<div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-3 border-r-3 border-t-5 border-l-transparent border-r-transparent border-t-white"></div>
							</div>
						)}
					</div>
				)}
			/>

			{/* <div className="flex justify-between text-sm text-msi-darkgrey-color mt-0">
				<span className="text-xs font-medium relative">{formatYearMonth(minDate)}</span>
				<span className="text-xs font-medium relative">Live</span>
			</div> */}
		</div>
	);
};

export default DateRangeSlider;
